import {
  type AdminOrderInquiryListInput,
  DEFAULT_LIMIT,
  ORDER_INQUIRY_KINDS,
  ORDER_INQUIRY_STATUSES,
  type OrderInquiryKind,
  type OrderInquiryStatus,
} from '@repo/api-contracts';
import { type ReactElement, useState } from 'react';
import { useOrderInquiriesListQuery } from '@/entities/order-inquiry';
import { Button, EmptyState, Pagination, tableStyles } from '@/shared/ui';
import { OrderInquiryListFilters } from './order-inquiry-list-filters';
import { OrderInquiryListRow } from './order-inquiry-list-row';

const COLUMNS = ['주문 번호', '담당자', '종류', '상태', '처리 결과', '사진', '접수일'] as const;

const statusOf = (value: string): OrderInquiryStatus | undefined =>
  ORDER_INQUIRY_STATUSES.find((status) => status === value);
const kindOf = (value: string): OrderInquiryKind | undefined =>
  ORDER_INQUIRY_KINDS.find((kind) => kind === value);

const toInput = (status: string, kind: string, page: number): AdminOrderInquiryListInput => {
  const parsedStatus = statusOf(status);
  const parsedKind = kindOf(kind);
  return {
    page,
    limit: DEFAULT_LIMIT,
    ...(parsedStatus ? { status: parsedStatus } : {}),
    ...(parsedKind ? { kind: parsedKind } : {}),
  };
};

/**
 * 어드민 문의 목록 — 상태(기본 처리 중)·종류 필터, 흰 표 카드, 페이지 이동(0건이면 숨김). 필터를 바꾸면 첫 쪽으로 돌아간다.
 * 상세는 미리 부르지 않는다(열 때마다 조회 기록이 남는다).
 */
export const OrderInquiryList = (): ReactElement => {
  const [status, setStatus] = useState('OPEN');
  const [kind, setKind] = useState('');
  const [page, setPage] = useState(1);
  const query = useOrderInquiriesListQuery(toInput(status, kind, page));
  const data = query.data;
  // 문의는 있는데 보던 쪽이 범위를 벗어나 비었으면(답변으로 건수가 줄어든 경우) "문의 없음"이 아니라 범위 밖이다.
  const outOfRange =
    data !== undefined &&
    data.items.length === 0 &&
    data.meta.totalPages > 0 &&
    data.meta.page > data.meta.totalPages;

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <OrderInquiryListFilters
          status={status}
          kind={kind}
          onStatusChange={(next) => {
            setStatus(next);
            setPage(1);
          }}
          onKindChange={(next) => {
            setKind(next);
            setPage(1);
          }}
        />
        {query.isFetching && data ? (
          <p role="status" className="ml-auto text-sm text-muted-foreground">
            불러오는 중…
          </p>
        ) : null}
      </div>

      <div className="mt-5">
        {data === undefined ? (
          query.isError ? (
            <EmptyState isError>목록을 불러오지 못했습니다: {query.error.message}</EmptyState>
          ) : (
            <EmptyState>불러오는 중…</EmptyState>
          )
        ) : outOfRange ? (
          <div className="flex flex-col items-center gap-4">
            <EmptyState>페이지 범위를 벗어났습니다.</EmptyState>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setPage(data.meta.totalPages)}
            >
              마지막 페이지로
            </Button>
          </div>
        ) : data.items.length === 0 ? (
          <EmptyState>조건에 맞는 문의가 없습니다.</EmptyState>
        ) : (
          <div className={tableStyles.card}>
            <table className={tableStyles.table}>
              <thead className={tableStyles.head}>
                <tr>
                  {COLUMNS.map((label) => (
                    <th key={label} className={tableStyles.th}>
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.items.map((inquiry) => (
                  <OrderInquiryListRow key={inquiry.id} inquiry={inquiry} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data !== undefined && !outOfRange ? (
        <Pagination
          className="mt-5 justify-center"
          page={data.meta.page}
          totalPages={data.meta.totalPages}
          onPageChange={setPage}
        />
      ) : null}
    </>
  );
};
